import { useState, useEffect, useRef, useCallback } from "react";
import {
  Layout,
  Typography,
  Button,
  Space,
  theme,
  message,
  Dropdown,
  Spin,
  Switch,
  Tooltip,
  Progress,
  Modal,
  Tabs
} from "antd";
import {
  ReloadOutlined,
  FileTextOutlined,
  DownloadOutlined,
  LoadingOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import dayjs from "dayjs";
import { AuditLogFilters } from "./components/AuditLogFilters";
import { AuditLogTable } from "./components/AuditLogTable";
import { AuditLogDetail } from "./components/AuditLogDetail";
import { AuditLogStats } from "./components/AuditLogStats";
import { fetchTraceLogs, transformTraceLogsResponse } from "./services/api";
import { BookingAnalyticsApi } from "./services/bookingAnalyticsApi";
import App from "../LayoutMobile";
import AnalyticsReport from "./analytics/AnalyticsReport";
import { BarChartOutlined, LineChartOutlined } from "@ant-design/icons";
import "./AuditLogs.css";

const AUTO_REFRESH_INTERVAL = 120000; 

const exportToCSV = (logs, filename) => {
  const headers = [
    "Session ID",
    "Module",
    "Log Name",
    "Status",
    "Severity",
    "Last Step",
    "Failed Step",
    "IP Address",
    "Started At",
    "Updated At",
  ];

  const rows = logs.map((log) => [
    log.correlationId,
    log.module || "",
    log.logName || "",
    log.status,
    log.severity || "",
    log.lastStep,
    log.failedStep || "",
    log.ipAddress || "",
    format(new Date(log.startedAt), "yyyy-MM-dd HH:mm:ss"),
    log.completedAt
      ? format(new Date(log.completedAt), "yyyy-MM-dd HH:mm:ss")
      : "",
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), "yyyyMMdd_HHmmss")}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const exportToJSON = (logs, filename) => {
  const jsonContent = JSON.stringify(logs, null, 2);
  const blob = new Blob([jsonContent], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), "yyyyMMdd_HHmmss")}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Index = () => {
  const { token } = theme.useToken();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [logsTableData, setLogsTableData] = useState([]);
  const intervalRef = useRef(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const getFilters = useCallback(() => {
    const filters = {};

    if (dateRange && dateRange[0] && dateRange[1]) {
      filters.startDate = dateRange[0].format("YYYY-MM-DD");
      filters.endDate = dateRange[1].format("YYYY-MM-DD");
    }
    if (debouncedSearch) {
      filters.search = debouncedSearch;
    }
    if (statusFilter && statusFilter !== "all") {
      filters.status = statusFilter;
    }
    if (moduleFilter && moduleFilter !== "all") {
      filters.module = moduleFilter;
    }

    return filters;
  }, [dateRange, debouncedSearch, statusFilter, moduleFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 700); // 500ms debounce
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadLogs = useCallback(
    async (page = 1, silent = false) => {
      if (!silent) setLoading(true);
      try {
        const filters = getFilters();
        const response = await fetchTraceLogs(page, filters);                
        const transformed = transformTraceLogsResponse(response?.resultData);
        console.log(transformed, "transformed");
        
        setLogs(response?.statsData);
        setLogsTableData(transformed?.logs);
        setPagination(transformed.pagination);
        setLastRefreshed(new Date());
      } catch (error) {
        console.error("Failed to fetch logs:", error);
        if (!silent) message.error("Failed to load trace logs");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [getFilters],
  );

  // Initial load and filter changes
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    loadLogs(1);
  }, [dateRange, debouncedSearch, statusFilter, moduleFilter, loadLogs]);

  // Page change handler (separate from filters)
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    loadLogs(page);
  }, [loadLogs]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        loadLogs(currentPage, true);
      }, AUTO_REFRESH_INTERVAL);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [autoRefresh, currentPage, loadLogs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Logs are now filtered server-side, no client-side filtering needed
  const filteredLogs = logsTableData;

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  // const handleExport = async (type) => {
  //   if (filteredLogs.length === 0) {
  //     message.warning("No logs to export");
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     const filters = getFilters();
  //     let allLogs = [];
  //     let allRawData = [];
  //     let hasMore = true;
  //     let page = 1;
  //     const perPage = 100;

  //     // Build query parameters
  //     const buildUrl = (pageNum) => {
  //       const params = new URLSearchParams();
  //       params.append("per_page", perPage);
        
  //       if (filters.startDate) {
  //         params.append("start_date", filters.startDate);
  //       }
  //       if (filters.endDate) {
  //         params.append("end_date", filters.endDate);
  //       }
  //       if (filters.status && filters.status !== "all") {
  //         params.append("status", filters.status);
  //       }
  //       if (filters.module && filters.module !== "all") {
  //         params.append("module", filters.module);
  //       }
  //       if (filters.search) {
  //         params.append("search", filters.search);
  //       }
        
  //       return `${API_BASE_URL}trace-logs/page/${pageNum}?${params.toString()}`;
  //     };

  //     // First, get the first page to determine total pages
  //     const firstResponse = await fetch(buildUrl(page), {
  //       method: "GET",
  //       headers: {
  //         Authorization: `Bearer ${localStorage.getItem("token")}`,
  //         "Content-Type": "application/json",
  //       },
  //     });

  //     if (!firstResponse.ok) {
  //       throw new Error("Failed to fetch logs");
  //     }

  //     const firstData = await firstResponse.json();
  //     console.log( "firstData",firstData);
  //     const totalPages = firstData?.data?.last_page || 1;
  //     const totalRecords = firstData?.data?.total || 0;
  //     console.log("totalPages", totalPages, totalRecords);
  //     message.loading({
  //       content: `Exporting page 1 of ${totalPages}...`,
  //       duration: 0,
  //     });

  //     // Process first page
  //     allRawData = [...allRawData, ...(firstData?.data?.data || [])];

  //     // Fetch remaining pages
  //     for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
  //       try {
  //         const response = await fetch(buildUrl(pageNum), {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${localStorage.getItem("token")}`,
  //             "Content-Type": "application/json",
  //           },
  //         });

  //         if (!response.ok) {
  //           throw new Error(`Failed to fetch page ${pageNum}`);
  //         }

  //         const pageData = await response.json();
  //         allRawData = [...allRawData, ...(pageData?.data?.data || [])];

  //         // Update progress message every 10 pages
  //         if (pageNum % 10 === 0 || pageNum === totalPages) {
  //           message.destroy();
  //           message.loading({
  //             content: `Exporting page ${pageNum} of ${totalPages}...`,
  //             duration: 0,
  //           });
  //         }
  //       } catch (error) {
  //         console.error(`Error fetching page ${pageNum}:`, error);
  //         message.destroy();
  //         message.error(`Failed to fetch page ${pageNum}`);
  //         setLoading(false);
  //         return;
  //       }
  //     }

  //     message.destroy();

  //     // Transform data for CSV if needed
  //     if (type === "csv") {
  //       allLogs = allRawData.map((log) => ({
  //         correlationId: log.session_id,
  //         module: log.module || "",
  //         logName: log.log_name || "",
  //         status: log.status,
  //         severity: log.severity || "",
  //         lastStep: log.steps?.[log.steps.length - 1]?.step || "",
  //         failedStep:
  //           log.steps?.find((s) => s.step_status === "failed")?.step || "",
  //         ipAddress: log.ip_address || "",
  //         startedAt: log.created_at,
  //         completedAt: log.updated_at,
  //       }));
  //     }

  //     // Export all collected logs
  //     if (type === "csv") {
  //       exportToCSV(allLogs, "trace_logs");
  //       message.success(`Successfully exported ${allRawData.length} logs to CSV`);
  //     } else {
  //       // For JSON, export raw data with all details including steps
  //       exportToJSON(allRawData, "trace_logs");
  //       message.success(`Successfully exported ${allRawData.length} logs to JSON`);
  //     }
  //   } catch (error) {
  //     console.error("Export error:", error);
  //     message.error("Failed to export logs");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
const handleExport = async (type) => {
  if (filteredLogs.length === 0) {
    message.warning("No logs to export");
    return;
  }

  setIsExporting(true);
  setShowProgress(true);
  setExportProgress(0);

  try {
    const filters = getFilters();
    let allRawData = [];
    const perPage = 100;

    // Build params for the booking-analytics trace-logs endpoint
    const buildParams = (pageNum) => {
      const params = { page: pageNum, per_page: perPage };
      if (filters.startDate) params.date_from = filters.startDate;
      if (filters.endDate)   params.date_to   = filters.endDate;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.module && filters.module !== "all") params.module = filters.module;
      if (filters.search)    params.search    = filters.search;
      return params;
    };

    // First page — determine total pages
    const firstRes = await BookingAnalyticsApi.getLogs(buildParams(1));
    if (!firstRes?.success) throw new Error(firstRes?.message || "Failed to fetch logs");

    const paginatedData = firstRes?.data;
    const totalPages = paginatedData?.last_page || 1;
    allRawData.push(...(paginatedData?.data || []));
    setExportProgress(Math.round((1 / totalPages) * 100));

    // Remaining pages
    for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
      const res = await BookingAnalyticsApi.getLogs(buildParams(pageNum));
      if (!res?.success) throw new Error(`Failed to fetch page ${pageNum}`);
      allRawData.push(...(res?.data?.data || []));
      setExportProgress(Math.round((pageNum / totalPages) * 100));
    }

    // Export collected data
    if (type === "csv") {
      const csvData = allRawData.map((log) => ({
        correlationId: log.session_id,
        module:        log.module       || "",
        logName:       log.log_name     || "",
        status:        log.status,
        severity:      log.severity     || "",
        lastStep:      log.steps?.[log.steps.length - 1]?.step || "",
        failedStep:    log.steps?.find((s) => s.step_status === "failed")?.step || "",
        ipAddress:     log.ip_address   || "",
        startedAt:     log.created_at,
        completedAt:   log.updated_at,
      }));
      exportToCSV(csvData, "trace_logs");
    } else {
      exportToJSON(allRawData, "trace_logs");
    }

    setExportProgress(100);
    message.success(`Successfully exported ${allRawData.length} logs`);

  } catch (error) {
    console.error("Export error:", error);
    message.error("Failed to export logs");
  } finally {
    setIsExporting(false);
    setTimeout(() => {
      setShowProgress(false);
      setExportProgress(0);
    }, 1000);
  }
};

  const handleRefresh = () => {
    loadLogs(currentPage);
    message.info("Refreshing logs...");
  };


  const handleAutoRefreshToggle = (checked) => {
    setAutoRefresh(checked);
    if (checked) {
      message.success("Auto-refresh enabled (every 30s)");
    } else {
      message.info("Auto-refresh disabled");
    }
  };

  const exportMenuItems = [
    { key: "csv", label: "Export as CSV", onClick: () => handleExport("csv") },
    {
      key: "json",
      label: "Export as JSON",
      onClick: () => handleExport("json"),
    },
  ];

  const traceLogsContent = (
    <Layout style={{ background: 'transparent' }}>
        {/* Header */}
        <Header
          style={{
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            padding: '0 24px',
            height: 'auto',
            lineHeight: 'normal',
            position: 'sticky',
            top: 48,
            zIndex: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            marginLeft: -24,
            marginRight: -24,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 0",
              gap: 16,
            }}
          >
            <Space size="middle" align="center">
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #008385 0%, #00b3b5 100%)',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: '0 2px 8px rgba(0,131,133,0.3)',
                  flexShrink: 0,
                }}
              >
                <FileTextOutlined style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, justifySelf: "left" }}>
                  Trace Logs
                </Title>
                <Text type="secondary" style={{ fontSize: 12, lineHeight: '1.4' }}>
                  Booking flow tracking &amp; diagnostics
                </Text>
              </div>
            </Space>
            <Space size={8}>
              {/* Auto-refresh toggle */}
              <Tooltip
                title={
                  autoRefresh
                    ? "Auto-refresh enabled (every 2 min)"
                    : "Enable auto-refresh"
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 12px',
                    borderRadius: 20,
                    background: autoRefresh
                      ? 'rgba(26,127,75,0.08)'
                      : '#f3f4f6',
                    border: `1px solid ${autoRefresh ? 'rgba(26,127,75,0.25)' : '#e5e7eb'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => handleAutoRefreshToggle(!autoRefresh)}
                >
                  <SyncOutlined
                    spin={autoRefresh}
                    style={{
                      fontSize: 12,
                      color: autoRefresh ? '#1a7f4b' : '#6b7280',
                    }}
                  />
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: autoRefresh ? '#1a7f4b' : '#6b7280',
                      userSelect: 'none',
                    }}
                  >
                    {autoRefresh ? 'Live' : 'Auto'}
                  </Text>
                  <Switch
                    size="small"
                    checked={autoRefresh}
                    onChange={handleAutoRefreshToggle}
                    style={{ '--ant-switch-checked-bg': '#1a7f4b' }}
                  />
                </div>
              </Tooltip>

              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                disabled={isExporting}
              >
                <Button
                  icon={isExporting ? <LoadingOutlined /> : <DownloadOutlined />}
                  loading={isExporting}
                  disabled={isExporting}
                  style={{
                    borderRadius: 8,
                    fontWeight: 500,
                  }}
                >
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </Dropdown>
              <Button
                icon={loading ? <LoadingOutlined /> : <ReloadOutlined />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
            </Space>
          </div>
        </Header>

        {/* Main Content */}
        <Content style={{ padding: '20px 24px' }}>
          <Spin spinning={loading} className="custom-spinner" tip="Loading trace logs...">
            <Space direction="vertical" size={20} style={{ width: "100%" }}>
              {/* Export Progress Modal */}
              <Modal
                open={showProgress}
                title={
                  <Space>
                    <SyncOutlined spin style={{ color: '#008385' }} />
                    <span>Exporting Logs</span>
                  </Space>
                }
                footer={null}
                closable={false}
                centered
              >
                <div style={{ padding: '8px 0 16px' }}>
                  <Progress
                    percent={exportProgress}
                    status={exportProgress === 100 ? "success" : "active"}
                    strokeWidth={10}
                    strokeColor={{ '0%': '#008385', '100%': '#00b3b5' }}
                  />
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block', textAlign: 'center' }}>
                    {exportProgress < 100 ? `Fetching data... ${exportProgress}%` : 'Export complete!'}
                  </Text>
                </div>
              </Modal>

              {/* Stats */}
              <AuditLogStats logs={logs} totalCount={pagination?.total}/>

              {/* Filters */}
              <div style={{
                background: '#fff',
                border: '1px solid #f0f0f0',
                borderRadius: 10,
                padding: '14px 18px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <AuditLogFilters
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  statusFilter={statusFilter}
                  onStatusChange={setStatusFilter}
                  moduleFilter={moduleFilter}
                  onModuleChange={setModuleFilter}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  debouncedSearch={debouncedSearch}
                />
              </div>

              {/* Results Count */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Showing <strong style={{ color: '#111827' }}>{filteredLogs.length}</strong> of{" "}
                  <strong style={{ color: '#111827' }}>{pagination?.total || 0}</strong> attempts
                </Text>
                {lastRefreshed && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Last updated: {format(lastRefreshed, "HH:mm:ss")}
                  </Text>
                )}
              </div>

              {/* Table */}
              <AuditLogTable
                logs={filteredLogs}
                onRowClick={handleRowClick}
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </Space>
          </Spin>
        </Content>

        {/* Detail Drawer */}
        <AuditLogDetail
          log={selectedLog}
          open={detailOpen}
          onClose={() => {
            setDetailOpen(false);
            setSelectedLog(null);
          }}
        />
      </Layout>
  );

  return (
    <App>
      <Tabs
        defaultActiveKey="1"
        className="audit-logs-tabs"
        items={[
          {
            key: '1',
            label: <span><BarChartOutlined />&nbsp;Analytics Report</span>,
            children: <AnalyticsReport />,
          },
          {
            key: '2',
            label: <span><FileTextOutlined />&nbsp;Trace Logs</span>,
            children: traceLogsContent,
          },
        ]}
      />
    </App>
  );
};

export default Index;
